/* FAB price-sensitivity questionnaire — tsk-2026-08-26-001.
   Van Westendorp, four questions, one at a time, on the /password success state.

   THE ONE THING TO KNOW BEFORE EDITING THIS FILE
   POST /contact requires a CAPTCHA token that Shopify's own script attaches only when a
   genuine submit event fires on a Liquid-rendered form (measured 2026-08-27: a bare
   fetch() is rejected 400 "Missing CAPTCHA token"). Two consequences that look like
   arbitrary choices below but are not:
     1. There is exactly ONE submission, after the fourth answer. Posting each answer in
        the background as it arrives is not implementable against this endpoint.
     2. The form is submitted by CLICKING a real submit control. form.submit() does not
        fire a submit event, so the captcha script never runs and the post fails.

   The signup itself is never at risk: the email is banked by Horizon's own form before
   this module is ever shown, and a repeat submission updates that same customer record
   (contact[tags] merges additively rather than replacing). */

(function () {
  'use strict';

  var KEY_EMAIL = 'fab-vw-email';
  var KEY_DONE = 'fab-vw-done';

  /* sessionStorage throws outright in some privacy modes rather than returning null. */
  function store(fn, fallback) {
    try {
      return fn();
    } catch (e) {
      return fallback;
    }
  }

  /* Takes an element out of both the accessibility tree AND the paint.
     The attribute alone is not enough and that is not theoretical: Horizon's
     `.email-signup__message { display: flex }` is an author rule, and it outranks the
     UA stylesheet's `[hidden] { display: none }` — which is how that line once shipped
     visible after being "hidden". The marker attribute is what the module's own
     stylesheet hangs a 0,2,0 `display: none` on, so the paint cannot be outranked by
     whatever Horizon declares on the element. */
  function hide(el) {
    el.hidden = true;
    el.setAttribute('data-fab-vw-hidden', '');
  }

  /* --- Page 1 -------------------------------------------------------------
     Stash the address as the signup form submits, so the success render can join
     the answers to it. Capture phase, read-only: it never interferes with the
     submission it is listening to. A data-fab-email attribute from Liquid is
     preferred when present; this is the fallback that does not depend on it. */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || form.nodeName !== 'FORM') return;
      if (form.closest('[data-fab-vw]')) return;
      var field = form.querySelector('input[name="contact[email]"]');
      if (!field || !field.value) return;
      var value = field.value.trim();
      store(function () {
        sessionStorage.setItem(KEY_EMAIL, value);
      });
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    var root = document.querySelector('[data-fab-vw]');
    if (!root) return;

    /* Only ever on the signup success state. No success marker, no questionnaire. */
    var success = document.querySelector('[data-fab-signup-success]');
    if (!success) return;

    var email =
      (success.getAttribute('data-fab-email') || '').trim() ||
      store(function () {
        return sessionStorage.getItem(KEY_EMAIL);
      }, '') ||
      '';

    /* Without an address the answers cannot be joined to anything, so ask nothing. */
    if (!email) return;

    /* Move it directly AFTER the signup block — as a SIBLING, never inside it.
       Horizon sets `.email-signup-block { min-width: fit-content }`. A four-panel flex
       track has a very large intrinsic width, so nesting the module inside that block
       made fit-content expand it far past its 50% and shoved the whole column off the
       left edge of the page (observed live 2026-08-28). As a sibling the module carries
       its own measure and cannot inflate anything upstream of it. */
    var host = success.closest('.email-signup-block');
    if (host && host.parentNode) {
      host.parentNode.insertBefore(root, host.nextSibling);
    }


    var form = root.querySelector('form');
    var emailField = root.querySelector('[data-vw-email]');
    if (!form || !emailField) return;
    emailField.value = email;

    root.hidden = false;

    /* Horizon's own "Thanks for subscribing!" line is now redundant: the module
       carries the confirmation itself, and closes on "All set, thanks for
       subscribing!". Hidden HERE rather than in CSS, and only once the module is
       actually on screen — if this script never runs, that line is the visitor's
       only confirmation that the signup worked, and it must survive. */
    success.hidden = true;

    /* The questionnaire REPLACES the signup block rather than stacking under it
       (Tioma, 2026-08-28). Left standing, the block re-rendered its heading, an
       empty email field and "Join waitlist" directly above the module's own input
       and "Reply" — two fields and two CTAs on one screen, with the live one the
       lower of the pair.

       Hidden only once the module is confirmed on screen, and only ever on the
       success render: the email is already banked by the time this runs, so there
       is nothing left to sign up for. Every path that returns before this point
       leaves the block untouched, which is what keeps a JS failure from taking the
       signup down with it.

       ⚠️ The `hidden` attribute is set for the accessibility tree, and the module's
       stylesheet carries a 0,2,0 `.email-signup-block[hidden]` rule to guarantee the
       paint. Horizon puts no author `display` on this block today, but it does on
       `.email-signup__message` — which is exactly how that line shipped visible once.
       Same defence, applied before it is needed rather than after. */
    if (host) {
      hide(host);

      /* The signup's own label goes with it — "Get early access to the FAB Workbench
         prototype" is a separate text block sitting immediately above the field, not
         part of the block, so hiding the block alone leaves it stranded directly above
         the module's input where it reads as that input's label. It is also an
         invitation to do something the visitor has just done.

         Deliberately expressed as "the text block immediately before the signup"
         rather than pinned to its theme-editor id (`__text-3`): the relationship is
         what makes it the signup's label, and an id is recreated whenever the block
         is re-added in the editor. Anything that is not a text block is left alone. */
      var label = host.previousElementSibling;
      if (label && label.classList.contains('text-block')) hide(label);

      /* The module's top margin existed to separate it from the block above it.
         With the block gone the module IS that position, so the margin would only
         push it down the page. */
      root.classList.add('fab-vw--replaces-signup');
    }

    /* Already answered this session — show the closing state, never re-ask. This is
       also what runs on the reload that the final submission itself causes. */
    if (
      store(function () {
        return sessionStorage.getItem(KEY_DONE);
      }, null) === '1'
    ) {
      finish(root, false);
      return;
    }

    wire(root, form);
  }

  function wire(root, form) {
    var track = root.querySelector('[data-vw-track]');
    var viewport = root.querySelector('[data-vw-viewport]');
    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-vw-step]'));
    var questions = Array.prototype.slice.call(root.querySelectorAll('[data-vw-question]'));
    if (!track || !viewport || !steps.length) return;

    var answers = [];
    var index = 0;

    /* Pin the viewport to the tallest step so the swipe never animates a height.
       Measured after reveal, remeasured on resize because the questions rewrap.

       RING_ALLOWANCE exists for one reason, measured on the live page 2026-08-28:
       the viewport is `overflow: hidden` (it has to be, or the other three steps
       would be visible), and the focused input's ring — 2px wide at 2px offset —
       overflowed the pinned box by 1.4px at the BOTTOM and was being sliced off.
       This is the trap Rule 2 named when the focus ring was ruled: an offset ring
       is clipped by any ancestor with overflow: hidden.

       The fix is to give the ring room, NOT to draw it inside the element —
       Rule 2 requires it offset into the ground, and un-offsetting it is a
       documented regression. 8px is generous against the 1.4px measured, so a
       longer question that rewraps cannot bring the clipping back. */
    var RING_ALLOWANCE = 8;

    function pin() {
      /* Reset before measuring — both boxes rewrap at a new width. */
      questions.forEach(function (question) {
        question.style.minHeight = '';
      });
      viewport.style.height = 'auto';

      /* Pin every question to the tallest of the four, so the input and Reply sit at
         the same height on all four steps. The full Van Westendorp wording is very
         unequal in length — question 3 runs to about twice question 2 — and without
         this the row slides up and down the pinned viewport as the wizard swipes,
         moving the target out from under a respondent who is answering at speed.
         min-height, not height: a narrow viewport may still need more room. */
      var tallestQuestion = 0;
      questions.forEach(function (question) {
        tallestQuestion = Math.max(tallestQuestion, question.offsetHeight);
      });
      if (tallestQuestion) {
        questions.forEach(function (question) {
          question.style.minHeight = tallestQuestion + 'px';
        });
      }

      var tallest = 0;
      steps.forEach(function (step) {
        tallest = Math.max(tallest, step.offsetHeight);
      });
      if (tallest) viewport.style.height = tallest + RING_ALLOWANCE + 'px';
    }
    pin();
    window.addEventListener('resize', pin);

    steps.forEach(function (step, i) {
      var input = step.querySelector('[data-vw-input]');
      var reply = step.querySelector('[data-vw-reply]');
      if (!input || !reply) return;

      reply.addEventListener('click', function () {
        submitAnswer(step, i, input, reply);
      });

      input.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter') return;
        /* Enter must advance, not submit: only the fourth answer submits, and it does
           so through the hidden submit control so the captcha token attaches. */
        event.preventDefault();
        submitAnswer(step, i, input, reply);
      });
    });

    function submitAnswer(step, i, input, reply) {
      if (i !== index) return;
      var error = step.querySelector('[data-vw-err]');
      var raw = String(input.value || '').replace(/[^0-9.]/g, '');
      var value = parseFloat(raw);

      if (!raw || !isFinite(value) || value <= 0) {
        say(error, 'Enter a price, in numbers.');
        input.focus({ preventScroll: true });
        return;
      }

      /* Coherence, enforced at the point of entry. The questions run cheap-first, so a
         valid set only ever ascends. Catching it here is why one-at-a-time is safe:
         a four-up form would only reveal an incoherent set after the fact, and an
         incoherent set is unusable for the curves. */
      var previous = answers.length ? answers[answers.length - 1].value : null;
      if (previous !== null && value <= previous) {
        say(error, 'That should be more than ' + format(previous) + '.');
        input.select();
        return;
      }

      say(error, '');
      answers.push({ key: step.getAttribute('data-vw-key'), value: value });

      reply.classList.add('is-confirmed');

      if (answers.length === steps.length) {
        complete(root, form, answers);
        return;
      }

      /* Advance and take focus in the SAME user gesture. Deferring the focus until the
         transition ends closes and reopens the mobile keyboard on every question, which
         on its own destroys the few-seconds feel this is built for. */
      index += 1;
      track.style.transform = 'translateX(' + -index * 100 + '%)';
      var next = steps[index].querySelector('[data-vw-input]');
      if (next) next.focus({ preventScroll: true });
    }

    function say(node, message) {
      if (node) node.textContent = message;
    }
  }

  function format(value) {
    return '$' + (Math.round(value * 100) / 100);
  }

  function complete(root, form, answers) {
    var tagsField = root.querySelector('[data-vw-tags]');

    /* Read the base tags off the SIGNUP form rather than trusting the snippet's
       default. The questionnaire's submission re-sends the attribution alongside
       the answers, so it has to match what the signup actually sent — byte for
       byte. Hardcoding was safe while /password was the only surface; from
       2026-08-28 the homepage carries a signup too, and the moment the two use
       different waitlist_source values a hardcoded base would bolt a second,
       wrong source tag onto the record. Tags merge additively, so that mistake
       would be permanent rather than overwritten.
       Scoped inside .email-signup-block so it cannot pick up our own field. */
    var signupTags = document.querySelector(
      '.email-signup-block input[name="contact[tags]"]'
    );
    var base = (
      (signupTags && signupTags.value) ||
      root.getAttribute('data-base-tags') ||
      ''
    ).split(',');
    var tags = base
      .concat(
        answers.map(function (answer) {
          return answer.key + ':' + answer.value;
        })
      )
      .filter(Boolean)
      .join(',');

    if (tagsField) tagsField.value = tags;

    store(function () {
      sessionStorage.setItem(KEY_DONE, '1');
    });

    finish(root, true);

    /* Let the closing checkmark land before the page navigates away. */
    var submit = root.querySelector('[data-vw-submit]');
    window.setTimeout(function () {
      /* A real click, not form.submit() — see the note at the top of this file. */
      if (submit) submit.click();
    }, 700);
  }

  function finish(root, animate) {
    var module = root.querySelector('[data-vw-module]');
    var done = root.querySelector('[data-vw-done]');
    if (module) module.hidden = true;
    if (!done) return;
    done.hidden = false;
    if (!animate) {
      done.classList.add('is-in');
      return;
    }
    /* Next frame, so the transition has a start value to move from. */
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        done.classList.add('is-in');
      });
    });
  }
})();
